import * as PopoverPrimitive from "@radix-ui/react-popover";
import { IconCrossLarge } from "central-icons/IconCrossLarge";
import { useCallback, useEffect, useRef, useState, type FC } from "react";

import { AnnouncementMarkdown } from "@/components/AnnouncementMarkdown";
import { SuperOnaAnimation } from "@/components/changelog/SuperOnaAnimation";
import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { Text } from "@/components/flexkit/typography/Text";
import { cn } from "@/components/podkit/lib/cn";
import type { ChangelogData } from "@/hooks/use-changelog";

/**
 * Renders a changelog thumbnail. SVG URLs are fetched and injected inline
 * so SMIL animations play. Raster images use a standard <img>. Handles
 * missing URLs and fetch/load errors gracefully.
 *
 * Uses aspect-video to reserve space and prevent layout shift while loading.
 */
const WidgetThumbnail: FC<{ src: string | undefined; alt: string }> = ({ src, alt }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [failed, setFailed] = useState(false);
    const isSvg = src?.endsWith(".svg");

    useEffect(() => {
        if (!src || !isSvg || !containerRef.current) return;

        let cancelled = false;
        fetch(src)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.text();
            })
            .then((svgText) => {
                if (!cancelled && containerRef.current) {
                    containerRef.current.innerHTML = svgText;
                    const svgEl = containerRef.current.querySelector("svg");
                    if (svgEl) {
                        svgEl.classList.add("size-full", "rounded-md");
                        svgEl.style.display = "block";
                    }
                }
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });

        return () => {
            cancelled = true;
        };
    }, [src, isSvg]);

    if (!src || failed) return null;

    const containerClass = "relative mt-3 aspect-video w-full overflow-hidden rounded-md border border-border-base";

    if (isSvg) {
        return <div ref={containerRef} className={containerClass} />;
    }

    return (
        <div className={containerClass}>
            <img
                src={src}
                alt={alt}
                className="absolute inset-0 size-full rounded-md object-cover"
                decoding="sync"
                onError={() => setFailed(true)}
            />
        </div>
    );
};

/** How far the popover extends past the sidebar's right edge (expanded mode). */
const SIDEBAR_OVERSHOOT = 40;
/** Fixed width when the sidebar is collapsed. */
const COLLAPSED_WIDTH = 320;

type ChangelogPreviewProps = {
    changelog: ChangelogData;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onLearnMore: () => void;
    /** Popover placement relative to the trigger. Defaults to "top". */
    side?: "top" | "right" | "bottom" | "left";
    /** Render the popover anchored to this trigger element. */
    children: React.ReactNode;
};

/**
 * Small popover card showing a preview of the latest changelog entry.
 * Uses Radix Popover for click-outside and Escape key dismissal.
 * "Learn more" opens the full changelog dialog.
 * "Try it" only appears when the changelog has an actionUrl.
 */
export const ChangelogPreview: FC<ChangelogPreviewProps> = ({
    changelog,
    open,
    onOpenChange,
    onLearnMore,
    side = "top",
    children,
}) => {
    const triggerRef = useRef<HTMLElement | null>(null);
    const [popoverWidth, setPopoverWidth] = useState<number>(COLLAPSED_WIDTH);
    const widgetImageSrc = changelog.widgetImageUrl || changelog.imageUrl;

    const measureSidebar = useCallback((node: HTMLElement | null) => {
        triggerRef.current = node;
        if (node) {
            const sidebar = node.closest<HTMLElement>('[data-testid="sidebar"]');
            if (sidebar) {
                setPopoverWidth(sidebar.offsetWidth + SIDEBAR_OVERSHOOT);
            }
        }
    }, []);

    // Collapsed sidebar: use fixed width. Expanded: sidebar width + overshoot.
    const width = side === "right" ? COLLAPSED_WIDTH : popoverWidth;

    // Toggle pointer-events on the Radix Popper wrapper div so it doesn't
    // block clicks on the sidebar when the popover is closed but force-mounted.
    //
    // We use a ref callback (instead of useEffect alone) because the Portal
    // mounts asynchronously — on the initial render the content node doesn't
    // exist yet, so a useEffect that runs once with open=false would miss it.
    // The ref callback fires as soon as the Portal inserts the content into
    // the DOM, ensuring the wrapper gets pointer-events:none immediately.
    const popoverContentRef = useRef<HTMLDivElement | null>(null);
    const openRef = useRef(open);
    openRef.current = open;

    const setPopoverContentRef = useCallback((node: HTMLDivElement | null) => {
        popoverContentRef.current = node;
        const wrapper = node?.closest<HTMLElement>("[data-radix-popper-content-wrapper]");
        if (wrapper) {
            wrapper.style.pointerEvents = openRef.current ? "" : "none";
        }
    }, []);

    useEffect(() => {
        const wrapper = popoverContentRef.current?.closest<HTMLElement>("[data-radix-popper-content-wrapper]");
        if (wrapper) {
            wrapper.style.pointerEvents = open ? "" : "none";
        }
    }, [open]);

    return (
        <PopoverPrimitive.Root
            open={open}
            onOpenChange={(nextOpen) => {
                // With forceMount the DismissableLayer is always active and can
                // fire spurious dismiss/toggle events even when the popover is
                // already closed. Ignore no-op transitions to prevent the open
                // state from oscillating.
                if (nextOpen === open) return;

                if (nextOpen && triggerRef.current) {
                    const sidebar = triggerRef.current.closest<HTMLElement>('[data-testid="sidebar"]');
                    if (sidebar) {
                        setPopoverWidth(sidebar.offsetWidth + SIDEBAR_OVERSHOOT);
                    }
                }
                onOpenChange(nextOpen);
            }}
        >
            <PopoverPrimitive.Trigger asChild ref={measureSidebar}>
                {children}
            </PopoverPrimitive.Trigger>
            {/*
             * Portal + forceMount renders the popover outside the sidebar DOM
             * tree. The collapsed sidebar applies transform-gpu on hover (for
             * the expand animation), which creates a new containing block and
             * breaks position:fixed on inline children — causing the popover
             * to be clipped by overflow:hidden and continuously repositioned.
             *
             * forceMount keeps the content pre-rendered so it opens instantly.
             * The Radix Popper wrapper (data-radix-popper-content-wrapper) uses
             * position:fixed and doesn't inherit pointer-events-none, so we
             * toggle pointer-events on it manually when closed.
             */}
            <PopoverPrimitive.Portal forceMount>
                <PopoverPrimitive.Content
                    side={side}
                    sideOffset={8}
                    align={side === "right" ? "center" : "end"}
                    alignOffset={side === "right" ? 0 : SIDEBAR_OVERSHOOT}
                    collisionPadding={16}
                    style={{ width }}
                    className={cn(
                        "z-50 flex select-none flex-col rounded-lg border border-border-base bg-surface-primary shadow-md",
                        !open && "pointer-events-none invisible opacity-0",
                    )}
                    data-testid="changelog-preview"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    ref={setPopoverContentRef}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
                        <Text className="text-sm font-semibold text-content-primary">What&apos;s new</Text>
                        <PopoverPrimitive.Close asChild>
                            <Button
                                variant="ghost"
                                size="xs"
                                className="-mr-1 p-1"
                                aria-label="Dismiss"
                                data-tracking-id="dismiss-changelog-preview"
                            >
                                <IconCrossLarge size={10} />
                            </Button>
                        </PopoverPrimitive.Close>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col px-5 pb-4 pt-3">
                        {/* Title */}
                        <Text className="text-md font-semibold text-content-primary">{changelog.title}</Text>

                        {/* Date */}
                        {changelog.date && (
                            <Text className="mt-0.5 text-sm text-content-quaternary">{changelog.date}</Text>
                        )}

                        {/* Hero media — live component or static thumbnail */}
                        {changelog.heroComponent === "SuperOnaAnimation" ? (
                            <div className="relative mt-3 aspect-video w-full overflow-hidden rounded-md border border-border-base">
                                <SuperOnaAnimation playing={open} className="absolute inset-0 size-full" />
                            </div>
                        ) : (
                            <WidgetThumbnail src={widgetImageSrc} alt={changelog.title} />
                        )}

                        {/* Description */}
                        <div className="mt-3 line-clamp-3 text-base text-content-secondary">
                            <AnnouncementMarkdown content={changelog.description} />
                        </div>

                        {/* Actions */}
                        <div className="mt-4 flex flex-col gap-2">
                            <Button
                                variant="secondary"
                                size="md"
                                className="w-full"
                                onClick={onLearnMore}
                                data-tracking-id="changelog-learn-more"
                            >
                                Learn more
                            </Button>
                            {changelog.actionUrl && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    className="w-full"
                                    asChild
                                    data-tracking-id="changelog-try-now"
                                >
                                    <Link to={changelog.actionUrl}>Try it</Link>
                                </Button>
                            )}
                        </div>
                    </div>
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    );
};
